const BLOCK_SELECTOR = '.wp-block-wdg-language-switcher';
const LINK_SELECTOR = '.wp-block-wdg-language-switcher__link';
const FOCUSABLE_SELECTOR =
	'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const normalizePathname = ( pathname ) => {
	let path = pathname || '/';

	if ( '/' !== path ) {
		path = path.replace( /\/+$/, '' ) || '/';
	}

	return path;
};

const isPathUnderBase = ( linkPathname, basePathname ) => {
	const linkPath = normalizePathname( linkPathname );
	const basePath = normalizePathname( basePathname );

	if ( '/' === basePath ) {
		return true;
	}

	return linkPath === basePath || linkPath.startsWith( `${ basePath }/` );
};

const getDestinationMatchScore = ( linkUrl, baseUrl, isExternal ) => {
	let base;

	try {
		base = new URL( baseUrl, window.location.href );
	} catch {
		return -1;
	}

	if ( isExternal ) {
		if ( linkUrl.origin !== base.origin ) {
			return -1;
		}

		return Math.max( normalizePathname( base.pathname ).length, 1 );
	}

	if ( linkUrl.origin !== base.origin ) {
		return -1;
	}

	if ( ! isPathUnderBase( linkUrl.pathname, base.pathname ) ) {
		return -1;
	}

	const basePath = normalizePathname( base.pathname );
	return '/' === basePath ? 0 : basePath.length;
};

const findDestinationForHref = ( href, destinations ) => {
	let linkUrl;

	try {
		linkUrl = new URL( href, window.location.href );
	} catch {
		return null;
	}

	let bestDestination = null;
	let bestScore = -1;

	destinations.forEach( ( destination ) => {
		const score = getDestinationMatchScore(
			linkUrl,
			destination.baseUrl,
			destination.isExternal
		);

		if ( score > bestScore ) {
			bestScore = score;
			bestDestination = destination;
		}
	} );

	return bestDestination;
};

const shouldIgnoreLinkClick = ( event, anchor ) => {
	if ( event.defaultPrevented || event.button !== 0 ) {
		return true;
	}

	if ( event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ) {
		return true;
	}

	const href = anchor.getAttribute( 'href' );
	if ( ! href ) {
		return true;
	}

	const lowerHref = href.toLowerCase();

	return (
		'#' === href.charAt( 0 ) ||
		lowerHref.startsWith( 'javascript:' ) ||
		lowerHref.startsWith( 'mailto:' ) ||
		lowerHref.startsWith( 'tel:' )
	);
};

const opensInNewTab = ( anchor ) => {
	const target = anchor.getAttribute( 'target' );
	return Boolean( target && '_blank' === target.toLowerCase() );
};

const navigateToUrl = ( url, openInNewTab ) => {
	if ( ! url ) {
		return;
	}

	if ( openInNewTab ) {
		window.open( url, '_blank', 'noopener,noreferrer' );
		return;
	}

	window.location.href = url;
};

const resolveAbsoluteHref = ( anchor ) => {
	return new URL( anchor.href, window.location.href ).href;
};

const readDestinationFromLink = ( link ) => {
	const baseUrl = link.dataset.baseUrl ?? '';

	if ( ! link.dataset.popupTitle || ! baseUrl ) {
		return null;
	}

	return {
		baseUrl,
		isExternal: link.dataset.isExternal === '1',
		popupTitle: link.dataset.popupTitle ?? '',
		popupBody: link.dataset.popupBody ?? '',
		popupPrompt: link.dataset.popupPrompt ?? '',
		popupStay: link.dataset.popupStay ?? '',
		popupContinue: link.dataset.popupContinue ?? '',
		popupReminder: link.dataset.popupReminder ?? '',
	};
};

const parseSwitcherLinks = ( links ) => {
	let currentBaseUrl = '';
	const destinations = [];

	links.forEach( ( link ) => {
		if ( link.getAttribute( 'aria-current' ) === 'page' ) {
			currentBaseUrl = link.dataset.baseUrl ?? '';
			return;
		}

		const destination = readDestinationFromLink( link );
		if ( destination ) {
			destinations.push( destination );
		}
	} );

	return { destinations, currentBaseUrl };
};

const pageInterceptors = [];
let pageClickListenerAttached = false;

const registerPageLinkInterceptor = ( interceptor ) => {
	pageInterceptors.push( interceptor );

	if ( pageClickListenerAttached ) {
		return;
	}

	pageClickListenerAttached = true;

	document.addEventListener(
		'click',
		( event ) => {
			const anchor = event.target.closest( 'a[href]' );

			if ( ! anchor || anchor.closest( '.wp-block-wdg-language-switcher__dialog' ) ) {
				return;
			}

			let targetUrl;

			try {
				targetUrl = resolveAbsoluteHref( anchor );
			} catch {
				return;
			}

			for ( const { dialog, interceptLink } of pageInterceptors ) {
				if ( dialog?.open ) {
					continue;
				}

				if ( interceptLink( event, anchor, targetUrl ) ) {
					return;
				}
			}
		},
		false
	);
};

const initLanguageSwitcher = ( block ) => {
	const button = block.querySelector( '.wp-block-wdg-language-switcher__button' );
	const list = block.querySelector( '.wp-block-wdg-language-switcher__list' );
	const dialog = block.querySelector( '.wp-block-wdg-language-switcher__dialog' );

	if ( ! button || ! list ) {
		return;
	}

	const links = list.querySelectorAll( LINK_SELECTOR );
	const { destinations, currentBaseUrl } = parseSwitcherLinks( links );

	const dialogText = dialog
		? {
				title: dialog.querySelector( '.wp-block-wdg-language-switcher__dialog-title' ),
				body: dialog.querySelector( '.wp-block-wdg-language-switcher__dialog-body' ),
				prompt: dialog.querySelector( '.wp-block-wdg-language-switcher__dialog-prompt' ),
				stay: dialog.querySelector( '.wp-block-wdg-language-switcher__dialog-stay' ),
				continue: dialog.querySelector(
					'.wp-block-wdg-language-switcher__dialog-continue'
				),
				reminder: dialog.querySelector(
					'.wp-block-wdg-language-switcher__dialog-reminder-text'
				),
				close: dialog.querySelector( '.wp-block-wdg-language-switcher__dialog-close' ),
		  }
		: null;

	let currentIndex = -1;
	let isOpen = false;
	let pendingNavigationUrl = '';
	let pendingOpenInNewTab = false;
	let lastFocusElement = null;
	let focusTrapHandler = null;

	const toggleDropdown = ( open ) => {
		const shouldOpen = open !== undefined ? open : ! isOpen;

		button.classList.toggle( 'is-active', shouldOpen );
		list.classList.toggle( 'is-active', shouldOpen );
		button.setAttribute( 'aria-expanded', shouldOpen ? 'true' : 'false' );

		if ( ! shouldOpen ) {
			currentIndex = -1;
		}

		isOpen = shouldOpen;
	};

	const focusLink = ( index ) => {
		if ( index < 0 || index >= links.length ) {
			return;
		}

		links[ index ].focus();
		currentIndex = index;
	};

	const getFocusableElements = () => {
		if ( ! dialog ) {
			return [];
		}

		return Array.from( dialog.querySelectorAll( FOCUSABLE_SELECTOR ) ).filter( ( el ) => {
			return ! el.disabled && el.offsetParent !== null;
		} );
	};

	const getMastheadBreakpoint = () => {
		const masthead = block.closest( '.masthead' );
		const parsed = parseInt( masthead?.dataset?.breakpoint ?? '', 10 );

		return Number.isNaN( parsed ) ? 768 : parsed;
	};

	const resetPendingNavigation = () => {
		pendingNavigationUrl = '';
		pendingOpenInNewTab = false;
	};

	const closeDialog = () => {
		if ( ! dialog?.open ) {
			return;
		}

		dialog.close();

		if ( lastFocusElement?.focus ) {
			lastFocusElement.focus();
		}

		lastFocusElement = null;
		resetPendingNavigation();

		if ( focusTrapHandler ) {
			dialog.removeEventListener( 'keydown', focusTrapHandler );
			focusTrapHandler = null;
		}
	};

	const fillDialog = ( config ) => {
		if ( ! dialogText ) {
			return;
		}

		const {
			title,
			body,
			prompt,
			stay,
			continue: continueBtn,
			reminder,
		} = dialogText;

		if ( title ) title.textContent = config.popupTitle ?? '';
		if ( body ) body.textContent = config.popupBody ?? '';
		if ( prompt ) prompt.textContent = config.popupPrompt ?? '';
		if ( stay ) stay.textContent = config.popupStay ?? '';
		if ( continueBtn ) continueBtn.textContent = config.popupContinue ?? '';
		if ( reminder ) reminder.textContent = config.popupReminder ?? '';
	};

	const attachFocusTrap = () => {
		const focusables = getFocusableElements();

		if ( ! dialog || focusables.length === 0 ) {
			return;
		}

		focusTrapHandler = ( event ) => {
			if ( event.key !== 'Tab' ) {
				return;
			}

			const first = focusables[ 0 ];
			const last = focusables[ focusables.length - 1 ];
			const current = document.activeElement;

			if ( event.shiftKey && current === first ) {
				event.preventDefault();
				last.focus();
			} else if ( ! event.shiftKey && current === last ) {
				event.preventDefault();
				first.focus();
			}
		};

		dialog.addEventListener( 'keydown', focusTrapHandler );
	};

	const openDialog = ( config ) => {
		if ( ! dialog ) {
			return;
		}

		fillDialog( config );
		pendingNavigationUrl = config.targetUrl ?? '';
		pendingOpenInNewTab = Boolean( config.openInNewTab );
		lastFocusElement = document.activeElement;

		toggleDropdown( false );

		if ( typeof dialog.showModal !== 'function' ) {
			navigateToUrl( pendingNavigationUrl, pendingOpenInNewTab );
			resetPendingNavigation();
			return;
		}

		dialog.showModal();
		dialogText?.stay?.focus();
		attachFocusTrap();
	};

	const interceptRegionalLink = ( event, anchor, targetUrl ) => {
		if ( ! dialog || destinations.length === 0 || shouldIgnoreLinkClick( event, anchor ) ) {
			return false;
		}

		const destination = findDestinationForHref( targetUrl, destinations );

		if ( ! destination || ( currentBaseUrl && destination.baseUrl === currentBaseUrl ) ) {
			return false;
		}

		event.preventDefault();
		openDialog( {
			...destination,
			targetUrl,
			openInNewTab: opensInNewTab( anchor ),
		} );

		return true;
	};

	const continueNavigation = () => {
		const url = pendingNavigationUrl;
		const openInNewTab = pendingOpenInNewTab;

		closeDialog();
		navigateToUrl( url, openInNewTab );
	};

	if ( dialog ) {
		dialog.addEventListener( 'cancel', ( event ) => {
			event.preventDefault();
			closeDialog();
		} );

		[ dialogText?.close, dialogText?.stay ].forEach( ( dismissButton ) => {
			dismissButton?.addEventListener( 'click', ( event ) => {
				event.preventDefault();
				closeDialog();
			} );
		} );

		dialogText?.continue?.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			continueNavigation();
		} );

		window
			.matchMedia( `(max-width: ${ getMastheadBreakpoint() }px)` )
			.addEventListener( 'change', () => {
				if ( dialog.open ) {
					closeDialog();
				}

				if ( isOpen ) {
					toggleDropdown( false );
				}
			} );
	}

	button.addEventListener( 'click', ( event ) => {
		event.preventDefault();
		toggleDropdown();
	} );

	button.addEventListener( 'keydown', ( event ) => {
		switch ( event.key ) {
			case 'Enter':
			case ' ':
				event.preventDefault();
				toggleDropdown( true );
				focusLink( 0 );
				break;
			case 'ArrowDown':
				event.preventDefault();
				if ( ! isOpen ) {
					toggleDropdown( true );
				}
				focusLink( 0 );
				break;
			case 'ArrowUp':
				event.preventDefault();
				if ( ! isOpen ) {
					toggleDropdown( true );
				}
				focusLink( links.length - 1 );
				break;
			case 'Escape':
				if ( isOpen ) {
					toggleDropdown( false );
					button.focus();
				}
				break;
		}
	} );

	list.addEventListener( 'keydown', ( event ) => {
		if ( ! isOpen ) {
			return;
		}

		switch ( event.key ) {
			case 'ArrowDown':
				event.preventDefault();
				focusLink( ( currentIndex + 1 ) % links.length );
				break;
			case 'ArrowUp':
				event.preventDefault();
				focusLink( ( currentIndex - 1 + links.length ) % links.length );
				break;
			case 'Escape':
				event.preventDefault();
				toggleDropdown( false );
				button.focus();
				break;
			case 'Tab':
				toggleDropdown( false );
				break;
		}
	} );

	list.addEventListener( 'click', ( event ) => {
		event.stopPropagation();

		const link = event.target.closest( LINK_SELECTOR );

		if ( ! link || link.getAttribute( 'aria-current' ) === 'page' || ! link.dataset.popupTitle ) {
			return;
		}

		interceptRegionalLink( event, link, link.dataset.targetUrl || link.href );
	} );

	document.addEventListener(
		'click',
		( event ) => {
			if ( isOpen && ! block.contains( event.target ) ) {
				toggleDropdown( false );
			}
		},
		true
	);

	if ( dialog && destinations.length > 0 ) {
		registerPageLinkInterceptor( {
			dialog,
			interceptLink: interceptRegionalLink,
		} );
	}
};

document.querySelectorAll( BLOCK_SELECTOR ).forEach( initLanguageSwitcher );
